import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getEmployees} from "../../../selectors/userSelector";
import Button from "../button/Button";
import {Form, Formik} from "formik";
import FormikLabeledInput from "../input/FormikLabeledInput";
import {FormikCheckBox} from "../checkbox/FormikCheckbox";
import Select from "../select/Select";
import {Modal} from "react-bootstrap";
import ModalBody from "../../modals-new/ModalBody";
import ModalFooter from "../../modals-new/ModalFooter";
import ModalHeader from "../../modals-new/ModalHeader";
import {buildConfig, validateConfig} from "./tableViewConfig";

// wartość "domyślnego widoku" w selekcie (ustawienia z propsów tabeli, bez zapisanej konfiguracji)
const DEFAULT_VIEW = "__default__";

function ViewNameForm({mode, handleSubmit, isSubmitting, onClose}) {
    const {t} = useTranslation();
    return (
        <Form>
            <ModalHeader
                title={t(mode === "rename" ? "renameView" : "saveViewAs")}
                onCloseClick={onClose}
            />
            <ModalBody>
                <FormikLabeledInput
                    name="name"
                    id="async-table-view-name"
                    type="text"
                    label={t("viewName")}
                />
                {mode === "saveAs" && (
                    <>
                        <small className="async-table-views-hint">
                            {t("tableViewSaveHint")}
                        </small>
                        <FormikCheckBox
                            name="options"
                            field="shared"
                            label={t("shareToAll")}
                        />
                    </>
                )}
            </ModalBody>
            <ModalFooter
                onCloseClick={onClose}
                closeText={t("cancel")}
                hasConfirmButton
                confirmText={t("save")}
                confirmButtonColor="success"
                onConfirmed={handleSubmit}
                submitting={isSubmitting}
            />
        </Form>
    );
}

/**
 * Modal nazwy widoku: "Zapisz widok jako" (z udostępnieniem) albo zmiana nazwy - formularz Formik.
 * z-index podbity ponad szufladę ustawień (_async-table-settings.scss); Enter zatwierdza (ModalFooter).
 */
function ViewNameModal({mode, initialName, settings, onSubmit, onClose}) {
    const {t} = useTranslation();

    const initialValues = useMemo(
        () => ({name: initialName ?? "", options: {shared: false}}),
        [initialName]
    );

    // nazwa 1-100 znaków, Config <= 100 KB (backend i tak zwróci 400)
    const validate = useCallback(
        (values) => {
            const error = validateConfig(values.name, buildConfig(settings));
            return error ? {name: t(error.key, error.params)} : {};
        },
        [settings, t]
    );

    const onFormSubmit = useCallback(
        async (values) => {
            const ok = await onSubmit(values.name, values.options.shared);
            if (ok) onClose();
        },
        [onSubmit, onClose]
    );

    return (
        <Modal
            show
            onHide={onClose}
            className="async-table-views-modal"
            backdropClassName="async-table-views-modal-backdrop">
            <Formik
                initialValues={initialValues}
                validate={validate}
                onSubmit={onFormSubmit}>
                {({handleSubmit, isSubmitting}) => (
                    <ViewNameForm
                        mode={mode}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        onClose={onClose}
                    />
                )}
            </Formik>
        </Modal>
    );
}

/**
 * Potwierdzenie w szufladzie (modal aplikacji otworzyłby się pod szufladą).
 */
function ViewConfirm({text, confirmText, onConfirm, onCancel}) {
    const {t} = useTranslation();
    return (
        <div className="async-table-views-confirm" role="alertdialog">
            <span>{text}</span>
            <div className="async-table-views-actions">
                <Button
                    type="button"
                    buttonStyle="text"
                    text={t("cancel")}
                    onClick={onCancel}
                />
                <Button
                    type="button"
                    buttonColor="error"
                    text={confirmText}
                    onClick={onConfirm}
                />
            </div>
        </div>
    );
}

/**
 * Menu własnego widoku: zmiana nazwy, udostępnienie / cofnięcie, usunięcie.
 */
function ViewMenu({view, onRename, onShareToggle, onDelete}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const onBlur = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
    }, []);

    const onToggle = useCallback(() => setOpen((prev) => !prev), []);

    const onRenameClick = useCallback(() => {
        setOpen(false);
        onRename();
    }, [onRename]);

    const onShareClick = useCallback(() => {
        setOpen(false);
        onShareToggle();
    }, [onShareToggle]);

    const onDeleteClick = useCallback(() => {
        setOpen(false);
        onDelete();
    }, [onDelete]);

    return (
        <div className="async-table-settings-group-menu" onBlur={onBlur}>
            <button
                type="button"
                className="async-table-settings-group-menu-toggle"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t("tableViewOptions", {name: view.Name})}
                onClick={onToggle}>
                <i className="fa-solid fa-ellipsis fa-fw" />
            </button>
            {open && (
                <div
                    role="menu"
                    className="async-table-settings-group-menu-list">
                    <Button
                        type="button"
                        buttonStyle="text"
                        text={t("renameView")}
                        onClick={onRenameClick}
                    />
                    <Button
                        type="button"
                        buttonStyle="text"
                        text={t(view.Shared ? "stopSharing" : "shareToAll")}
                        onClick={onShareClick}
                    />
                    <Button
                        type="button"
                        buttonStyle="text"
                        text={t("deleteView")}
                        onClick={onDeleteClick}
                    />
                </div>
            )}
        </div>
    );
}

/**
 * Sekcja "Widok" w ustawieniach tabeli: wybór zapisanego widoku (moje i udostępnione), stan "zmieniony",
 * zapis, "Zapisz jako…", przywrócenie domyślnego i akcje własnego widoku wg uprawnień (IsOwner).
 * @param {*} param0
 * @returns
 */
export default function AsyncTableViews({
    views,
    settings,
    form,
    setForm,
    onRestoreDefault,
}) {
    const {t} = useTranslation();
    const employees = useSelector(getEmployees);

    // potwierdzenie: {type: "delete" | "unshare"}
    const [confirm, setConfirm] = useState(null);

    const {active, changed, items} = views;

    const authorName = useCallback(
        (ownerId) => {
            const user = employees?.find(
                (item) => item.LocalUserID === ownerId
            );
            const name = user
                ? `${user.firstname ?? ""} ${user.surname ?? ""}`.trim()
                : "";
            // autor spoza listy pracowników (np. usunięty) - tylko informacja, że widok jest udostępniony
            return name || user?.userName || t("sharedTableView");
        },
        [employees, t]
    );

    // opcje: domyślny, moje, udostępnione przez innych (z autorem); ikona przy udostępnionych
    const options = useMemo(() => {
        const mine = items.filter((item) => item.IsOwner);
        const others = items.filter((item) => !item.IsOwner);
        return [
            {value: DEFAULT_VIEW, name: t("defaultView")},
            ...[...mine, ...others].map((item) => ({
                value: item.ConfigID,
                name: item.IsOwner
                    ? `${item.Name} · ${t("mine")}`
                    : `${item.Name} · ${authorName(item.OwnerID)}`,
                shared: item.Shared,
            })),
        ];
    }, [items, authorName, t]);

    const optionRenderer = useCallback(
        (option) => (
            <span className="async-table-views-option">
                {option.shared && (
                    <i className="fa-solid fa-share-nodes fa-fw" />
                )}
                {option.name}
            </span>
        ),
        []
    );

    const onSelect = useCallback(
        (value) => {
            setForm(null);
            setConfirm(null);
            views.select(!value || value === DEFAULT_VIEW ? null : value);
        },
        [views, setForm]
    );

    const onSaveAsClick = useCallback(
        () => setForm({mode: "saveAs"}),
        [setForm]
    );

    const onFormCancel = useCallback(() => setForm(null), [setForm]);

    const onFormSubmit = useCallback(
        (name, shared) =>
            form?.mode === "rename"
                ? views.rename(active.ConfigID, name)
                : views.saveAs(name, shared),
        [form, views, active]
    );

    const onRename = useCallback(() => setForm({mode: "rename"}), [setForm]);

    // cofnięcie udostępnienia - inni użytkownicy tracą widok od razu, więc z potwierdzeniem
    const onShareToggle = useCallback(() => {
        if (active.Shared) setConfirm({type: "unshare"});
        else views.share(active.ConfigID, true);
    }, [active, views]);

    const onDelete = useCallback(() => setConfirm({type: "delete"}), []);

    const onConfirm = useCallback(async () => {
        const type = confirm?.type;
        setConfirm(null);
        if (type === "unshare") await views.share(active.ConfigID, false);
        if (type === "delete") await views.remove(active.ConfigID);
    }, [confirm, views, active]);

    const onConfirmCancel = useCallback(() => setConfirm(null), []);

    return (
        <section className="async-table-settings-section async-table-views">
            <div className="async-table-settings-section-header">
                <h6>{t("resultsDetailsTutorial.tableViewHeader")}</h6>
            </div>
            <div className="async-table-views-picker">
                <div className="async-table-views-select">
                    <Select
                        options={options}
                        value={active?.ConfigID ?? DEFAULT_VIEW}
                        onChange={onSelect}
                        customRenderer={optionRenderer}
                        clearButton={false}
                        insertFirstOnNoSelected={false}
                        callOnChangeOnSingleOption={false}
                    />
                </div>
                {changed && (
                    <span className="async-table-views-changed">
                        {t("changed")}
                    </span>
                )}
                {active?.IsOwner && (
                    <ViewMenu
                        view={active}
                        onRename={onRename}
                        onShareToggle={onShareToggle}
                        onDelete={onDelete}
                    />
                )}
            </div>
            {active && !active.IsOwner && (
                <small className="async-table-views-hint">
                    {t("tableViewSharedReadOnly")}
                </small>
            )}
            {confirm && (
                <ViewConfirm
                    text={t(
                        confirm.type === "delete"
                            ? active?.Shared
                                ? "deleteSharedViewConfirm"
                                : "deleteViewConfirm"
                            : "stopSharingTableViewConfirm",
                        {name: active?.Name}
                    )}
                    confirmText={t(
                        confirm.type === "delete" ? "deleteView" : "stopSharing"
                    )}
                    onConfirm={onConfirm}
                    onCancel={onConfirmCancel}
                />
            )}
            {form && (
                <ViewNameModal
                    key={form.mode}
                    mode={form.mode}
                    initialName={form.mode === "rename" ? active?.Name : ""}
                    settings={settings}
                    onSubmit={onFormSubmit}
                    onClose={onFormCancel}
                />
            )}
            <div className="async-table-views-actions">
                <Button
                    type="button"
                    icon={<i className="fa-solid fa-floppy-disk" />}
                    text={t("save")}
                    disabled={!active?.IsOwner || !changed}
                    onClick={views.save}
                />
                <Button
                    type="button"
                    text={t("saveAs")}
                    onClick={onSaveAsClick}
                />
                <Button
                    type="button"
                    buttonStyle="text"
                    icon={<i className="fa-solid fa-rotate-left" />}
                    text={t("restoreDefault")}
                    onClick={onRestoreDefault}
                />
            </div>
        </section>
    );
}
